import { useEffect, useRef, useState } from "react";
import { IonContent, IonItem, IonList, IonModal, IonRadio, IonRadioGroup } from "@ionic/react";
import { PositionModel } from "model";
import { Colors } from "swing-components";

import { translate } from "~utils";

import { ModalHeader } from "../ModalHeader";
import { SelectItem } from "../SelectItem";

type PositionFieldProps = {
  /** The options to select */
  positions: PositionModel[];
  /** The selected option */
  selectedPosition: PositionModel;
  onChange: (item: PositionModel) => void;
  isDisabled?: boolean;
};

export function PositionField(props: PositionFieldProps) {
  /*** Props ***/
  const { positions, selectedPosition, onChange, isDisabled } = props;

  /*** Refs ***/
  const positionModal = useRef<HTMLIonModalElement>(null);

  /*** State ***/
  const [temporarySelectedPosition, setTemporarySelectedPosition] = useState(selectedPosition);

  /** Effects */
  /** Reset the temporary state when the `selectedPositions` prop changes */
  useEffect(() => {
    setTemporarySelectedPosition(selectedPosition);
  }, [selectedPosition]);

  /*** Handlers ***/
  /**
   * When dismissing the Modal, reset the temporary state and do not pass along
   * the change to the parent
   */
  const handleModalDismiss = () => {
    setTemporarySelectedPosition(selectedPosition);
    positionModal.current?.dismiss();
  };

  /** When confirming the Modal, pass long the changes to the parent */
  const handleModalConfirm = () => {
    onChange(temporarySelectedPosition);
    positionModal.current?.dismiss();
  };

  /*** Render ***/
  return (
    <SelectItem
      id="position-select-modal"
      label={translate("LABEL_POSITION_FIELD")}
      // Using `selectedPosition` since we do not want to change this name until
      // it's no longer a temporary change.
      value={selectedPosition.displayName}
      isDisabled={isDisabled}
      stacked
      css={isDisabled ? { color: Colors.slate300, cursor: "not-allowed" } : undefined}
    >
      <IonModal
        aria-label="Position Selection"
        trigger="position-select-modal"
        ref={positionModal}
        // TODO: Should we have this action not save the users choice?
        // Handles when clicking on the background
        onDidDismiss={handleModalDismiss}
      >
        <ModalHeader
          title={translate("LABEL_POSITION_FIELD")}
          // Handles when clicking on "Cancel"
          onModalDismiss={handleModalDismiss}
          // Handles when clicking on "Done"
          onModalConfirm={handleModalConfirm}
        />
        <IonContent>
          <IonList>
            <IonRadioGroup value={temporarySelectedPosition?.id}>
              {positions?.map((position: PositionModel) => {
                return (
                  // When selecting an option in the modal, set the temporary state since the user
                  // may still dismiss the modal which would ignore these change.
                  <IonItem key={position.id} onClick={() => setTemporarySelectedPosition(position)}>
                    <IonRadio labelPlacement="end" justify="start" value={position.id}>
                      {position.displayName}
                    </IonRadio>
                  </IonItem>
                );
              })}
            </IonRadioGroup>
          </IonList>
        </IonContent>
      </IonModal>
    </SelectItem>
  );
}
